<template>
  <div class="c2c-warn-overlay" role="dialog" aria-modal="true" :aria-labelledby="headingId">
    <div class="c2c-warn-card">
      <div class="c2c-warn-head">
        <AlertTriangle :size="20" />
        <h2 :id="headingId">{{ t('warnings.heading') }}</h2>
      </div>
      <p class="c2c-warn-intro">{{ t('warnings.intro') }}</p>
      <ul class="c2c-warn-list">
        <li v-for="line in lines" :key="line.code">
          <strong>{{ line.label }}</strong>
          <span v-if="line.detail"> - <code>{{ line.detail }}</code></span>
        </li>
      </ul>
      <p class="c2c-warn-question">{{ t('warnings.question') }}</p>
      <div class="c2c-warn-actions">
        <CdxButton @click="$emit('cancel')">{{ t('warnings.cancel') }}</CdxButton>
        <CdxButton
          action="progressive"
          weight="primary"
          @click="$emit('confirm')"
        >{{ t('warnings.confirm') }}</CdxButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { AlertTriangle } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton } from "@wikimedia/codex"

const { t } = useI18n()

const props = defineProps<{
  warnings: Record<string, unknown>
}>()
defineEmits<{ cancel: []; confirm: [] }>()

const headingId = "c2c-warn-heading-" + Math.random().toString(36).slice(2, 8)

interface WarnLine { code: string; label: string; detail?: string }

const lines = computed<WarnLine[]>(() => {
  const out: WarnLine[] = []
  for (const code of Object.keys(props.warnings)) {
    const value = props.warnings[code]
    const detail = formatDetail(code, value)
    out.push({
      code,
      label: t('warnings.code_' + code, t('warnings.code_unknown', { code })),
      detail
    })
  }
  return out
})

function formatDetail(code: string, value: unknown): string | undefined {
  if (typeof value === "string") return value
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "object" && value !== null) {
    // 'duplicate' returns an array; 'exists' returns a filename string;
    // 'badfilename' returns a string. Other shapes are JSON-stringified.
    try { return JSON.stringify(value) } catch { /* */ }
  }
  if (code === "exists" && typeof value === "string") return value
  return undefined
}
</script>

<style scoped>
.c2c-warn-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, 0.4);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.c2c-warn-card {
  background: white; border-radius: 8px;
  max-width: 560px; width: 100%;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-height: 90vh; overflow-y: auto;
}
.c2c-warn-head {
  display: flex; align-items: center; gap: 10px;
  color: #886600; margin-bottom: 12px;
}
.c2c-warn-head h2 { margin: 0; font-size: 18px; }
.c2c-warn-intro { margin: 0 0 12px 0; font-size: 14px; color: #54595d; }
.c2c-warn-list {
  margin: 0 0 12px 0; padding-left: 20px;
  background: #fef6e7; border: 1px solid #f0d795; border-radius: 4px;
  padding: 12px 12px 12px 32px;
}
.c2c-warn-list li { margin-bottom: 6px; font-size: 13px; }
.c2c-warn-list li:last-child { margin-bottom: 0; }
.c2c-warn-list code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: white; padding: 1px 4px; border-radius: 3px;
  word-break: break-all;
}
.c2c-warn-question { font-weight: 600; margin: 0 0 16px 0; font-size: 14px; }
.c2c-warn-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
@media (max-width: 540px) {
  .c2c-warn-actions { flex-direction: column-reverse; }
  .c2c-warn-actions > * { width: 100%; }
}
</style>
